import { TableRow } from '@sam/components';
import { NavigateFunction } from 'react-router-dom';
import { Currency, CurrencyConversion } from 'shared/src/currency/Currency.types';
import i18n from '../../i18n/i18n';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Util method to convert Currencies into TableEntries
 * @param currencies to convert
 * @param navigate navigateFunction to navigate to edit
 * @returns  Array of TableRows
 */
export const convertCurrencyIntoTableEntries = (
  currencies: Currency[],
  navigate: NavigateFunction,
): TableRow[] => {
  return currencies.map((currency) => ({
    id: currency.id,
    onClick: () => navigate('/currency/edit', { state: { currency } }),
    content: [
      currency.symbol,
      currency.title,
      currency.intlCode,
      currency.disabled
        ? i18n.t('pages.currency.overview.deactivated')
        : i18n.t('pages.currency.overview.active'),
      <div>
        <IconComponent
          type={IconType.EDIT}
          width={30}
          onClick={(evt) => {
            evt.stopPropagation();
            navigate('/currency/edit', { state: { currency } });
          }}
        />
      </div>,
    ],
  }));
};

/**
 * Util method to create a new currency object
 * @param override partial of currency to adjust created object
 * @returns generated currency object
 */
export const generateEmptyCurrency = (override?: Partial<Currency>): Currency => ({
  id: undefined!,
  createDate: new Date(),
  createdBy: '',
  disabled: false,
  intlCode: '',
  lastUpdate: new Date(),
  symbol: '',
  title: '',
  updatedBy: '',
  ...override,
});

/**
 * Util method to convert currencyConversions into TableEntries
 * @param conversions Array of currencyConversion
 * @returns Array of TableRows
 */
export const convertConversionsIntoTableEntries = (
  conversions: CurrencyConversion[],
  setConversion: (conversion: CurrencyConversion) => void,
): TableRow[] => {
  return conversions.map((conversion) => ({
    id: conversion.id,
    content: [
      conversion.createDate.toLocaleDateString('DE-de'),
      conversion.period?.startDate.toLocaleDateString('DE-de'),
      conversion.period?.endDate.toLocaleDateString('DE-de'),
      conversion.factor,
    ],
    onClick: () => setConversion(conversion),
  }));
};

/**
 * Util method to generate an empty currencyConversion
 * @param override partial conversion to adjust the created object
 * @returns generated currencyConversion
 */
export const generateEmptyCurrencyConversion = (
  override: Partial<CurrencyConversion>,
): CurrencyConversion => ({
  factor: 1,
  currencyId: '',
  period: { startDate: new Date(), endDate: new Date() },
  createDate: undefined!,
  id: undefined!,
  ...override,
});
